import { useCallback, useState } from 'react';
import type { WebViewMessageEvent } from 'react-native-webview';
import { logger as LOG } from '@sweetgreen/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useApplePayErrorHandling } from './useApplePayErrorHandling';

LOG.enable('ApplePay');
const logger = LOG.extend('ApplePay');

export const useApplePayMessages = (props: ApplePayMessagesHandlerProps) => {
  const { onCapturePaymentMethodId } = props;
  const handleApplePayError = useApplePayErrorHandling();

  const [isApplePayEnabled, setIsApplePayEnabled] = useState(false);
  const [isApplePayDisabled, setIsApplePayDisabled] = useState(false);
  const { track } = useTelemetry();

  const handleApplePayMessages = useCallback(
    async (event: WebViewMessageEvent) => {
      const message = parseApplePayMessage(event);
      const { paymentMethodId, applePayEnabled, applePayDisabled, error } =
        message;

      logger.info(message);
      track('apple-pay-message', message);

      if (error) {
        handleApplePayError(error);
      }

      if (applePayEnabled) {
        setIsApplePayEnabled(true);
        setIsApplePayDisabled(false);
      }

      if (applePayDisabled) {
        setIsApplePayEnabled(false);
        setIsApplePayDisabled(true);
      }

      if (paymentMethodId) {
        await onCapturePaymentMethodId?.(paymentMethodId);
      }
    },
    [handleApplePayError, onCapturePaymentMethodId, track],
  );

  return { isApplePayEnabled, isApplePayDisabled, handleApplePayMessages };
};

const parseApplePayMessage = (event: WebViewMessageEvent) => {
  const dataString = event.nativeEvent.data ?? '{}';
  const parsedData = JSON.parse(dataString) as HostedFieldsPayload;

  return parsedData;
};

// ─── Types ─────────────────────────────────────────────────────────────────────

type ApplePayMessagesHandlerProps = Readonly<{
  onCapturePaymentMethodId?: (paymentMethodId: string) => Promise<void>;
}>;

type HostedFieldsPayload = Readonly<{
  log?: string;
  paymentMethodId?: string;
  applePayEnabled?: boolean;
  applePayDisabled?: boolean;
  error?: { message?: string };
}>;
